import * as React from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Dialog } from "@progress/kendo-react-dialogs";
import { Field, Form, FormElement } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { MultiSelect, AutoComplete } from "@progress/kendo-react-dropdowns";
import { Checkbox, Input, Switch } from "@progress/kendo-react-inputs";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Error } from "@progress/kendo-react-labels";
import { getter } from "@progress/kendo-react-common";
import { passwordValidation } from "../utils/passwordValidation";
import { deleteField } from "../actions";

const lastNameGetter = getter("lastName");
const firstNameGetter = getter("firstName");
const passwordGetter = getter("password");
const emailGetter = getter("email");
const emailRegex = new RegExp(/\S+@\S+\.\S+/);

const formValidator = (values, props) => {
  var emailVerified = emailRegex.test(emailGetter(values));
  var lNameVerified = lastNameGetter(values) ? lastNameGetter(values).trim() : lastNameGetter(values)
  var fNameVerified = firstNameGetter(values) ? firstNameGetter(values).trim() : firstNameGetter(values)

  var passwordVerified = [true, ''];

  if (passwordGetter(values)) {
    passwordVerified = passwordValidation(passwordGetter(values));
  }

  if (emailVerified && passwordVerified[0] && lNameVerified && fNameVerified) {
    return;
  }

  return {
    VALIDATION_SUMMARY: "",
    ...!emailVerified && { ["email"]: "Invalid Email Specified." },
    ...!passwordVerified[0] && { ["password"]: passwordVerified[1] },
    ...!lNameVerified && { ["lastName"]: "Last Name cannot be empty." },
    ...!fNameVerified && { ["firstName"]: "First Name cannot be empty." },
  };
};

const FormInput = (fieldRenderProps) => {
  const { validationMessage, visited, ...others } = fieldRenderProps;
  return (
    <div>
      <Input {...others} />
      {
        visited && validationMessage && <Error direction={"end"} style={{ flexDirection: 'column' }}>{validationMessage}</Error>
      }
    </div>
  );
};



const EditUserForm = (props) => {
  const [displayAdditional, setDisplayAdditional] = React.useState(false);
  const [additionalFields, setAdditionalFields] = React.useState(props.item && props.item.AdditionalDetails ? props.item.AdditionalDetails : []);
  const [agency, setAgency] = React.useState(props?.item?.agency ?? '');
  const [isAdmin, setIsAdmin] = React.useState(false);
  const { token } = useSelector(state => state.authReducer);

  if (props.item.trainingDate) {
    props.item.trainingDate = new Date(props.item.trainingDate);
  }

  const submit = (event, props) => {
    let outputFields = additionalFields.filter((field) => field.propertyName.trim().length != 0 && field.description.trim().length != 0);
    props.onSubmit({ ...event, additionalDetails: outputFields, agency: agency }, event.Groups);
  }

  const generateEmptyField = (e) => {
    e.preventDefault()
    setAdditionalFields([...additionalFields, { propertyName: '', description: '' }]);
  }

  const FormAutoComplete = (fieldRenderProps) => {
    const { validationMessage, visited, ...others } = fieldRenderProps;

    return (
      <div>
        <AutoComplete 
          data={props?.agencies?.filter(a => agency ? a.toLowerCase().startsWith(agency) : true)} 
          value={agency} 
          onChange={(e) => {setAgency(e.value)}}
          onClose={(e) => setAgency(e.value)}
          placeholder="Agency"
          suggest={true}
          id="agency"
          {...others}
          //style={{ width: '73%', position: 'relative' }}
        />

        {visited && validationMessage && <Error>{validationMessage}</Error>}
      </div>
    )
  }

  
  const FormMultiSelect = (fieldRenderProps) => {
    const { validationMessage, visited, ...others } = fieldRenderProps;

    return (
      <div>
        <MultiSelect
          data={props.groups}
          textField="name"
          dataItemKey="id"
          {...others} />
        {visited && validationMessage && <Error>{validationMessage}</Error>}
      </div>
    )
  }
  
  const FormMultiSelectSME = (fieldRenderProps) => {
    const { validationMessage, visited, value, ...others } = fieldRenderProps;
    const needsOptions = ['Analysis (Crime, Intelligence)', 'Aviation (Drones, Airplane, Helicopter)', 'Arson', 'Bankwatch', 'Canine Detection', 'CBRNE', 'Child sexual exploitation', 'Civil Disturbance', 'Crime Scene Investigations / Forensic Analysis (includes Underwater search)', 'Criminal or Death Investigations (Polygraph)', 'Crisis Negotiation', 'Community outreach (Homeless Liaison, Resistance Education Programs)', 'Cyber', 'Executive Protection', 'Detention', 'Financial Crimes', 'Fraud / Theft / Robbery', 'Fugitive Apprehension', 'General or Specialized Instructor', 'Hazardous Devices and Explosives', 'Honor Guard', 'Human Trafficking', 'Internal Affairs / Special Investigations', 'Linguist', 'Marine (Harbor)', 'Mental Health Assistance', 'Mounted', 'Narcotics / Drug Investigations', 'NC Western NC Network', 'Organized Crime or Gangs', 'Patrol (Traffic, Bicycle, Marine, Motorcycle, DUI, Crash, Rail, Harbor)', 'Special Victims (Sexual assault, kidnapping, family violence)', 'School Safety', 'Technical Services', 'Threat Assessment', 'Special Weapons and Tactics', 'Workplace Violence', 'Other'];
    let valOutput = value;
    if (valOutput && !Array.isArray(valOutput)) {
      valOutput = JSON.parse(valOutput)
    }
    return (
      <div>
        <MultiSelect
          data={needsOptions}
          value={valOutput}
          {...others} />
        {visited && validationMessage && <Error>{validationMessage}</Error>}
      </div>
    )
  }

  const toggleView = (e) => {
    e.preventDefault();
    if (displayAdditional) {
      setDisplayAdditional(false);
    } else {
      setDisplayAdditional(true);
    }
  }

  const fieldValueChange = (e, index, type) => {
    let fieldsClone = structuredClone(additionalFields);
    if (type === 'name') {
      fieldsClone[index].propertyName = e.value
    } else {
      fieldsClone[index].description = e.value
    }
    setAdditionalFields(fieldsClone);
  }

  const removeField = (e, index) => {
    e.preventDefault();

    let fieldsClone = structuredClone(additionalFields);
    fieldsClone.splice(index, 1);
    setAdditionalFields(fieldsClone);
  }

  const adminFormSwitch = (fieldRenderProps) => {
    return FormSwitch(fieldRenderProps, "Admin", props.item.admin)
  }

  const enabledFormSwitch = (fieldRenderProps) => {
    return FormSwitch(fieldRenderProps, "Enabled", props.item.enabled)
  }

  const azureFormSwitch = (fieldRenderProps) => {
    return FormSwitch(fieldRenderProps, "Azure", props.item.azure)
  }

  const pwResetSwitch = (fieldRenderProps) => {
    return FormSwitch(fieldRenderProps, "Password Reset", props.item.passwordResetReq)
  }

  const limitedSwitch = (fieldRenderProps) => {
    return FormSwitch(fieldRenderProps, "Limited", props.item.canOnlySeeContacts)
  }

  const groupRestrictionSwitch = (fieldRenderProps) => {
    return FormSwitch(fieldRenderProps, "Group Restriction", props.item.canOnlySeeGroupContacts)
  }

  const viewOnlySwitch = (fieldRenderProps) => {
    return FormSwitch(fieldRenderProps, "View Only", props.item.viewOnly);
  }

  const notificationOnlySwitch = (fieldRenderProps) => {
    return FormSwitch(fieldRenderProps, "Notification Only", props.item.notificationOnly);
  }

  const floSwitch = (fieldRenderProps) => {
    return FormSwitch(fieldRenderProps, "FLO", props.item.flo);
  }

  const cfrSwitch = (fieldRenderProps) => {
    return FormSwitch(fieldRenderProps, "28 CFR Completed", props.item.cfrComplete)
  }

  const floInfoSwitch = (fieldRenderProps) => {
    return FormSwitch(fieldRenderProps, "FLO Information Sent", props.item.floInfo)
  }
  
  const floDate = (fieldRenderProps) => {
    return FormDate(fieldRenderProps)
  }
  
  const FormDate = (fieldRenderProps) => {
    const { validationMessage, visited, ...others } = fieldRenderProps;
    return (
      <div>
        <DatePicker popupSettings={{ popupClass: 'date-override' }}{...others} />
        {visited && validationMessage && <Error>{validationMessage}</Error>}
      </div>
    ); 
  }

  const FormSwitch = (fieldRenderProps, label, defaultCheck) => {
    const { validationMessage, visited, disabled, ...others } = fieldRenderProps;

    return (
      <div>
        <Switch disabled={disabled} defaultChecked={defaultCheck} {...others} />
        <label
          style={{ paddingLeft: '10px' }}
          className="k-checkbox-label">
          {label}
        </label>
        {visited && validationMessage && <Error>{validationMessage}</Error>}
      </div>
    );
  }
  return <Dialog
    title={props.item.id ? 'Edit User' : 'Create User'}
    style={{ zIndex: 2 }}
    onClose={props.cancelEdit}
  >
    <Form
      style={{ maxHeight: '600px', minWidth: '500px' }}
      onSubmit={(e) => submit(e, props)}
      initialValues={props.item}
      ignoreModified={true}
      validator={(v) => formValidator(v, props)}
      render={(formRenderProps) => (
        <FormElement style={styles.formElement}>
          {displayAdditional ?
            <fieldset className={"k-form-fieldset"}>
              <div style={{ paddingBottom: '10px' }}>
                <button className="k-button k-primary" style={styles.modalBtn} onClick={generateEmptyField}>
                  Add Field
                </button>
              </div>
              <div style={{ height: '410px', overflowY: 'scroll' }}>
                {additionalFields.map((field, index) => {
                  return (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-around', paddingBottom: '10px', alignItems: 'flex-end' }}>
                        <Input value={field.propertyName} onChange={(e) => fieldValueChange(e, index, 'name')} label={"Property Name"} style={{ width: '40%' }} />
                        <Input value={field.description} onChange={(e) => fieldValueChange(e, index, 'value')} label={"Value"} style={{ width: '40%' }} />
                        <Button
                          className="k-button k-flat"
                          onClick={(e) => removeField(e, index)}
                          iconClass="k-icon k-i-trash"
                          style={{ color: 'red' }}
                        />
                      </div>
                      {
                        ((field.propertyName.trim() && !field.description.trim()) || (!field.propertyName.trim() && field.description.trim())) &&
                          <Error style={{ paddingLeft: '10px' }}>
                            {"Both fields must have a value or this entry will not be saved."}
                          </Error>
                      }
                    </>
                  )
                })}
              </div>
            </fieldset> :
            <fieldset className={"k-form-fieldset"}>
              <div style={{ height: '460px', paddingRight: '10px', overflowY: 'scroll' }}>
                <div style={styles.accountDiv}>
                  <div className="k-window-title k-dialog-title">
                    Account
                  </div>
                  <div className="mb-3" style={styles.formField}>
                    <Field name={"email"} component={FormInput} label={"Email"} />
                  </div>
                  <div className="mb-3" style={formRenderProps.valueGetter("azure") ? { ...styles.formField, display: 'none' } : styles.formField}>
                    <Field name={"password"} component={FormInput} label={"Password"} />
                  </div>
                  <div className="mb-3" style={styles.formField}>
                    <Field
                      name={"admin"}
                      component={adminFormSwitch}
                      setIsActive={setIsAdmin}
                      isActive={isAdmin}
                      label={"Admin"} />
                    <text style={{ fontStyle: 'italic', fontSize: '12px', paddingLeft: '40px' }}>
                      Allows the user to access the admin dashboard.
                    </text>
                    <Field
                      name={"enabled"}
                      component={enabledFormSwitch}
                      label={"Enabled"} />
                    <text style={{ fontStyle: 'italic', fontSize: '12px', paddingLeft: '40px' }}>
                      Grants a user the ability to login.
                    </text>
                    <Field
                      name={"azure"}
                      component={azureFormSwitch}
                      label={"SSO"} />
                    <text style={{ fontStyle: 'italic', fontSize: '12px', paddingLeft: '40px' }}>
                      Indicates that the user has an email with SSO support.
                    </text>
                    <Field
                      name={"passwordResetReq"}
                      component={pwResetSwitch}
                      label={"Password Reset"} />
                    <text style={{ fontStyle: 'italic', fontSize: '12px', paddingLeft: '40px' }}>
                      Requires the user to change their password on their next login.
                    </text>
                    <Field
                      name={"canOnlySeeContacts"}
                      component={limitedSwitch}
                      label={"Limited"} />
                    <text style={{ fontStyle: 'italic', fontSize: '12px', paddingLeft: '40px' }}>
                      Only allows a user to view contacts within the mobile app.
                    </text>
                    <Field
                      name={"canOnlySeeGroupContacts"}
                      component={groupRestrictionSwitch}
                      label={"Group Restriction"} />
                    <text style={{ fontStyle: 'italic', fontSize: '12px', paddingLeft: '40px' }}>
                      Only allows the user to view contacts within their own group.
                    </text>
                    <Field
                      name={"viewOnly"}
                      disabled={isAdmin}
                      component={viewOnlySwitch}
                      label={"View/Notification Only"} />
                    <text style={{ fontStyle: 'italic', fontSize: '11px', paddingLeft: '40px' }}>
                      Removes any edit / deletion privledges a user may have access to. 
                    </text>
                    {/*<Field 
                      name={"notificationOnly"}
                      component={notificationOnlySwitch}
                      label={"Notification Only"}
                    />
                    <text style={{  display: 'block', fontStyle: 'italic', fontSize: '12px', paddingLeft: '40px' }}>
                      Only allows the user to send notifications. This also requires the 
                    </text>
                    <text style={{  fontStyle: 'italic', fontSize: '12px', paddingLeft: '40px' }}>
                      Admin switch to be active to have any effect.
                    </text>*/}
                  </div>
                </div>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"firstName"} component={FormInput} label={"First Name"} />
                </div>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"lastName"} component={FormInput} label={"Last Name"} />
                </div>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"phoneNumber"} component={FormInput} label={"Primary Phone"} />
                </div>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"title"} component={FormInput} label={"Title"} />
                </div>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"agency"} component={FormAutoComplete} label={"Agency"} />
                </div>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"agencyAddress"} component={AutoComplete} data={props.addressDataSource} textField={'text'} onChange={props.addressHandler} label={"Agency Address"} />
                </div>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"county"} component={FormInput} label={"County"} />
                </div>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"sme"} component={FormMultiSelectSME} label={"Subject Matter Expert"}/>
                </div>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"Groups"} component={FormMultiSelect} label={"Groups"} />
                </div>
                <div className="mb-3" style={styles.formField}>
                  <Field name={"excludedGroups"} component={FormMultiSelect} label={"Excluded Groups"} />
                  {/*<text style={{ fontStyle: 'italic', fontSize: '12px', paddingLeft: '40px' }}>
                    Users from selected groups will NOT be able to see this user
                  </text>*/}
                </div>
                <div className="mb-3" style={{ ...styles.formField, paddingLeft: '10px' }}>
                  <Field name={"flo"} component={floSwitch} label={"FLO Individual"} />
                </div>
                {props.currentUser?.site?.sitePermissions?.some(perm => perm.permissionName === 'FLO_FIELDS') && <>
                  <div className="mb-3" style={{ ...styles.formField, paddingLeft: '10px' }}>
                    <Field name={"cfrComplete"} component={cfrSwitch} label={"28 CFR Complete"} />
                  </div>
                  <div className="mb-3" style={{ ...styles.formField, paddingLeft: '10px' }}>
                    <Field name={"floInfo"} component={floInfoSwitch} label={"FLO Information Sent"} />
                  </div><div className="mb-3" style={{ ...styles.formField, paddingLeft: '10px' }}>
                    <Field name={"trainingDate"} component={floDate} label={"Training Date"} />
                  </div>
                </>}
              </div>
            </fieldset>}
          <div className="k-form-buttons k-justify-content-end" style={{ paddingBottom: '10px' }}>
            <button className="k-button k-primary" style={styles.modalBtn} onClick={toggleView}>
              {displayAdditional ? 'Edit User Information' : 'Add Additional Fields'}
            </button>
            <button type={"submit"} className="k-button k-primary" style={styles.modalBtn} disabled={false}>
              Save
            </button>
            <button type={"submit"} className="k-button" style={styles.modalBtn} onClick={props.cancelEdit}>
              Cancel
            </button>
          </div>
        </FormElement>
      )}
    />
  </Dialog>
}

const styles = {
  formElement: {
    width: 465,
    maxHeight: 525,
    minHeight: 525
  },
  formField: {
    marginTop: 10,
    marginBottom: 25,
  },
  formLabel: {
    fontWeight: 'bold',
    fontSize: 18,
  },
  adminSection: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'space-around',
    flexDirection: 'row'
  },
  modalBtn: {
    padding: '10px 15px'
  },
  accountDiv: {
    backgroundColor: '#F5F5F5',
    padding: '8px',
    border: '2px solid #DCDCDC',
    borderRadius: '6px'
  }
  //    padding: 8px 24px;
}

export default EditUserForm;